import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import Alert from '../common/Alert';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import useApi from '../../hooks/useApi';
import { FaBuilding, FaCity, FaMapMarkerAlt } from 'react-icons/fa';
import { CommunityAvatar } from '../common/CommunityIdentity';
import useLegalVersions from '../../hooks/useLegalVersions';

/**
 * Signup form component with a three-step registration process.
 * Step 1: Find and select a city.
 * Step 2: Search active communities by name in that city.
 * Step 3: Fill out user details and submit.
 */
function Signup() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    mobileNo: '',
    name: '',
    houseNo: '',
    division: '',
    ownershipType: '',
    email: '',
    communityId: '',
    acceptedTerms: false,
    privacyConsentGiven: false,
    displayName: '',
    propertyId: '',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityQuery, setCityQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [hasSearchedCities, setHasSearchedCities] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCommunities, setFilteredCommunities] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [communityStructure, setCommunityStructure] = useState({ levels: [], units: [], properties: [] });
  const [structureSelections, setStructureSelections] = useState({});
  const [propertySearch, setPropertySearch] = useState('');
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    mobileNo: '',
    communityName: '',
    message: '',
  });
  const navigate = useNavigate();
  const { get, post } = useApi();
  const legalVersions = useLegalVersions();

  useEffect(() => {
    setStructureSelections({});
    setPropertySearch('');
    setFormData(previous => ({
      ...previous,
      propertyId: '',
      houseNo: '',
      division: '',
    }));
    if (!formData.communityId) {
      setCommunityStructure({ levels: [], units: [], properties: [] });
      return;
    }
    get(`/api/public/community-structure/${formData.communityId}`)
      .then(setCommunityStructure)
      .catch(() =>
        setCommunityStructure({ levels: [], units: [], properties: [] })
      );
  }, [formData.communityId, get]);

  const orderedLevels = [...communityStructure.levels].sort(
    (left, right) => left.level_order - right.level_order
  );
  const structureSteps = [];
  let parentUnitId = null;
  let hierarchyComplete = true;
  let selectedUnitId = null;
  for (const level of orderedLevels) {
    const options = communityStructure.units.filter(
      unit =>
        String(unit.structure_level_id) === String(level.id) &&
        (parentUnitId === null
          ? unit.parent_unit_id == null
          : String(unit.parent_unit_id) === String(parentUnitId))
    );
    if (!options.length) continue;
    const selected = structureSelections[level.id] || '';
    structureSteps.push({ level, options, selected });
    if (!selected || !options.some(option => String(option.id) === String(selected))) {
      hierarchyComplete = false;
      break;
    }
    selectedUnitId = selected;
    parentUnitId = selected;
  }
  const propertySelectionReady =
    communityStructure.units.length === 0 ||
    (hierarchyComplete && selectedUnitId !== null);
  const eligibleProperties = communityStructure.properties.filter(property =>
    communityStructure.units.length === 0
      ? property.structure_unit_id == null
      : String(property.structure_unit_id) === String(selectedUnitId)
  );
  const normalizedPropertySearch = propertySearch.trim().toLowerCase();
  const propertySearchRequired =
    eligibleProperties.length > 40 && normalizedPropertySearch.length === 0;
  const matchingProperties = (
    propertySearchRequired
      ? []
      : eligibleProperties.filter(property =>
          `${property.display_name || ''} ${property.property_number || ''}`
            .toLowerCase()
            .includes(normalizedPropertySearch)
        )
  ).slice(0, 75);

  const selectStructureUnit = (levelId, value) => {
    const levelIndex = orderedLevels.findIndex(
      level => String(level.id) === String(levelId)
    );
    setStructureSelections(previous => {
      const next = {};
      orderedLevels.forEach((level, index) => {
        if (index < levelIndex && previous[level.id]) {
          next[level.id] = previous[level.id];
        }
      });
      if (value) next[levelId] = value;
      return next;
    });
    setPropertySearch('');
    setFormData(previous => ({
      ...previous,
      propertyId: '',
      houseNo: '',
      division: '',
    }));
  };

  const selectProperty = value => {
    const property = communityStructure.properties.find(
      item => String(item.id) === String(value)
    );
    const unit = communityStructure.units.find(
      item => String(item.id) === String(property?.structure_unit_id)
    );
    setFormData(previous => ({
      ...previous,
      propertyId: value,
      houseNo: property?.property_number || '',
      division: unit?.name || '',
    }));
  };

  const handleCitySearch = async () => {
    if (cityQuery.trim().length < 2) {
      setError('Enter at least two characters to search for a city.');
      return;
    }
    setError('');
    setHasSearchedCities(true);
    try {
      const response = await get('/api/communities/active/cities', {
        params: { search: cityQuery.trim() },
      });
      setCities(Array.isArray(response) ? response : []);
    } catch (err) {
      setCities([]);
      setError(err.message || 'Cities could not be loaded. Please try again.');
    }
  };

  const loadCommunities = async (city, search) => {
    setHasSearched(true);
    setError('');
    try {
      const response = await get('/api/communities/active', {
        params: { city, search: search.trim() },
      });
      const communityList = Array.isArray(response) ? response : [];
      setCommunities(communityList);
      setFilteredCommunities(communityList);
    } catch (err) {
      setCommunities([]);
      setFilteredCommunities([]);
      setError(err.message || 'Communities could not be loaded. Please try again.');
    }
  };

  const handleCitySelect = city => {
    setSelectedCity(city);
    setSearchQuery('');
    setCommunities([]);
    setFilteredCommunities([]);
    setHasSearched(false);
    setError('');
    setFormData(previous => ({ ...previous, communityId: '', displayName: '' }));
    setStep(2);
  };

  const handleSearch = async () => {
    if (!selectedCity) {
      setError('Please select a city first.');
      return;
    }
    if (searchQuery.trim().length < 3) {
      setHasSearched(false);
      setFilteredCommunities([]);
      setError('Enter at least three characters of the community name.');
      return;
    }
    await loadCommunities(selectedCity, searchQuery);
  };

  // Handle clear button click
  const handleClear = () => {
    setSearchQuery('');
    setFilteredCommunities([]);
    setFormData(prev => ({ ...prev, communityId: '', displayName: '' }));
    setHasSearched(false);
    setError('');
  };

  const backToCitySearch = () => {
    setStep(1);
    setSelectedCity('');
    setSearchQuery('');
    setCommunities([]);
    setFilteredCommunities([]);
    setHasSearched(false);
    setShowContactForm(false);
    setFormData(previous => ({ ...previous, communityId: '', displayName: '' }));
    setError('');
  };

  // Check username availability
  const checkUsername = async (username) => {
    if (!username) {
      setUsernameStatus(null);
      return;
    }

    try {
      const response = await get('/api/auth/check-username', { params: { username } });
      setUsernameStatus(response.available ? 'available' : 'taken');
    } catch (err) {
      setUsernameStatus(null);
      setError('Failed to check username availability.');
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    if (name === 'username') {
      setUsernameStatus(null);
      checkUsername(value);
      setFormData(prev => ({ ...prev, [name]: newValue }));
    } else if (name === 'searchQuery') {
      setSearchQuery(value);
      setHasSearched(false);
      setFilteredCommunities([]);
      setFormData(prev => ({ ...prev, communityId: '', displayName: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: newValue }));
    }
  };

  // Handle community selection
  const handleCommunitySelect = (community) => {
    const communityId = community.id || community._id || community.communityId;
    const displayName = community.displayName || '';
    if (!communityId) {
      setError('Selected community is invalid. Please select another community.');
      toast.error('Invalid community.');
      return;
    }
    if (!displayName) {
      setError('Selected community does not have a display name. Please select another community.');
      toast.error('Selected community does not have a display name.');
      return;
    }
    setFormData(prev => ({
      ...prev,
      communityId,
      displayName,
    }));
  };

  // Handle contact form input changes
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({ ...prev, [name]: value }));
  };

  // Proceed to next step
  const handleNext = () => {
    if (!formData.communityId) {
      setError('Please select a community.');
      toast.error('Please select a community.');
      return;
    }
    if (!formData.displayName) {
      setError('Selected community does not have a display name. Please select another community.');
      toast.error('Selected community does not have a display name.');
      return;
    }
    setError('');
    setStep(3);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!formData.acceptedTerms || !formData.privacyConsentGiven) {
      setError('Accept the Terms of Service and provide privacy consent to sign up.');
      toast.error('Both legal acknowledgements are required.');
      return;
    }

    try {
      await post('/api/auth/signup', formData);
      setMessage('Your request is pending approval.');
      toast.success('Signup successful!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Registration failed. Please contact Admin.';
      const errorDetails = err.response?.data?.details || '';
      const fullError = errorDetails ? `${errorMessage} ${errorDetails}` : errorMessage;
      setError(fullError);
      toast.error(fullError);
    }
  };

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await post('/api/auth/contact-admin', contactFormData);
      setMessage('Your request has been sent to the administrator.');
      toast.success('Request sent successfully!');
      setContactFormData({ name: '', email: '', mobileNo: '', communityName: '', message: '' });
      setShowContactForm(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to send request. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="container mt-5 animate__animated animate__fadeIn">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow">
            <div className="card-body">
              <h3 className="text-center mb-4">Sign Up</h3>
              {message && <Alert type="success" message={message} />}
              {error && <Alert type="danger" message={error} />}

              {step === 1 && !showContactForm && (
                <div className="signup-discovery">
                  <div className="signup-step-heading">
                    <span className="signup-step-icon"><FaCity /></span>
                    <div>
                      <small>Step 1 of 3</small>
                      <h5>Find your city</h5>
                      <p>Only cities with an active MyCommunity community are shown.</p>
                    </div>
                  </div>
                  <div className="mb-3">
                    <FormInput
                      label="City"
                      name="cityQuery"
                      value={cityQuery}
                      onChange={event => {
                        setCityQuery(event.target.value);
                        setHasSearchedCities(false);
                        setCities([]);
                      }}
                      placeholder="Example: Ahmedabad"
                      required
                    />
                    <div className="d-flex gap-2">
                      <Button
                        variant="primary"
                        className="w-50"
                        onClick={handleCitySearch}
                      >
                        Search cities
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="w-50"
                        onClick={() => {
                          setCityQuery('');
                          setCities([]);
                          setHasSearchedCities(false);
                          setError('');
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  {hasSearchedCities && cities.length > 0 && (
                    <div className="signup-city-grid" aria-label="Matching cities">
                      {cities.map(city => (
                        <button key={city} type="button" onClick={() => handleCitySelect(city)}>
                          <FaMapMarkerAlt /> <span>{city}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {hasSearchedCities && cities.length === 0 && (
                    <div className="signup-empty-state">No active communities were found in a matching city.</div>
                  )}
                </div>
              )}

              {step === 2 && !showContactForm && (
                <div className="signup-discovery">
                  <div className="signup-step-heading">
                    <span className="signup-step-icon"><FaBuilding /></span>
                    <div>
                      <small>Step 2 of 3</small>
                      <h5>Choose your community</h5>
                      <p>Search by community name in <strong>{selectedCity}</strong>.</p>
                    </div>
                  </div>
                  <FormInput
                    label="Community name"
                    name="searchQuery"
                    value={searchQuery}
                    onChange={handleChange}
                    placeholder="Enter at least 3 characters"
                    minLength={3}
                    required
                  />
                  <div className="d-flex gap-2 mb-3">
                    <Button variant="primary" className="w-50" onClick={handleSearch}>Search</Button>
                    <Button variant="outline-secondary" className="w-50" onClick={handleClear}>Clear</Button>
                  </div>
                  {hasSearched && filteredCommunities.length > 0 && (
                    <div className="signup-community-grid">
                      {filteredCommunities.map(community => {
                        const id = community.id || community._id || community.communityId;
                        const selected = String(formData.communityId) === String(id);
                        return (
                          <button
                            type="button"
                            key={id}
                            className={selected ? 'is-selected' : ''}
                            onClick={() => handleCommunitySelect(community)}
                          >
                            <CommunityAvatar community={community} size="md" />
                            <span>
                              <strong>{community.name || 'Community'}</strong>
                              <small>{community.area}, {community.city}</small>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {hasSearched && filteredCommunities.length === 0 && (
                    <div className="signup-empty-state">
                      No active communities match your search in {selectedCity}.
                      <Button variant="outline-primary" className="w-100 mt-3" onClick={() => setShowContactForm(true)}>
                        Request a new community
                      </Button>
                    </div>
                  )}
                  <div className="d-flex gap-2 mt-3">
                    <Button variant="outline-secondary" className="w-50" onClick={backToCitySearch}>Back</Button>
                    <Button variant="primary" className="w-50" onClick={handleNext} disabled={!formData.communityId}>
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {step === 2 && showContactForm && (
                <form onSubmit={handleContactSubmit}>
                  <FormInput
                    label="Your Name"
                    name="name"
                    value={contactFormData.name}
                    onChange={handleContactChange}
                    required
                  />
                  <FormInput
                    label="Your Email"
                    name="email"
                    type="email"
                    value={contactFormData.email}
                    onChange={handleContactChange}
                    required
                  />
                  <FormInput
                    label="Your Mobile Number"
                    name="mobileNo"
                    type="tel"
                    value={contactFormData.mobileNo}
                    onChange={handleContactChange}
                    required
                    pattern="[0-9]{10}"
                  />
                  <FormInput
                    label="Requested Community Name"
                    name="communityName"
                    value={contactFormData.communityName}
                    onChange={handleContactChange}
                    required
                  />
                  <FormInput
                    label="Message"
                    name="message"
                    type="textarea"
                    value={contactFormData.message}
                    onChange={handleContactChange}
                    required
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 mb-3"
                    disabled={
                      !contactFormData.name ||
                      !contactFormData.email ||
                      !contactFormData.mobileNo ||
                      !contactFormData.communityName ||
                      !contactFormData.message
                    }
                  >
                    Send Request
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="w-100"
                    onClick={() => setShowContactForm(false)}
                  >
                    Back to Community Selection
                  </Button>
                </form>
              )}

              {step === 3 && (
                <div>
                  {formData.displayName && (
                    <Alert
                      type="info"
                      message={`You have selected ${formData.displayName} community for signup.`}
                    />
                  )}
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6">
                        <FormInput
                          label="Username"
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          required
                          error={usernameStatus === 'taken' ? 'Username is already taken' : undefined}
                        />
                        {usernameStatus === 'available' && (
                          <div className="text-success mb-3">Username is available</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <FormInput
                          label="Password"
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <FormInput
                          label="Mobile Number"
                          name="mobileNo"
                          type="tel"
                          value={formData.mobileNo}
                          onChange={handleChange}
                          required
                          pattern="[0-9]{10}"
                        />
                      </div>
                      <div className="col-md-6">
                        <FormInput
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <FormInput
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      {communityStructure.properties.length === 0 && (
                        <div className="col-md-6">
                          <FormInput
                            label="House Number"
                            name="houseNo"
                            value={formData.houseNo}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      )}
                    </div>
                    {communityStructure.properties.length > 0 && (
                      <section className="signup-property-picker mb-3">
                        <div className="signup-property-heading">
                          <div>
                            <strong>Select your home or unit</strong>
                            <small>
                              Choose the address sections first, then search by
                              property number or name.
                            </small>
                          </div>
                          {formData.propertyId && (
                            <span className="badge bg-success">Selected</span>
                          )}
                        </div>
                        <div className="row g-3">
                          {structureSteps.map(({ level, options, selected }) => (
                            <div className="col-md-6" key={level.id}>
                              <label
                                className="form-label"
                                htmlFor={`structure-level-${level.id}`}
                              >
                                {level.singular_label}
                              </label>
                              <select
                                id={`structure-level-${level.id}`}
                                className="form-select"
                                value={selected}
                                onChange={event =>
                                  selectStructureUnit(
                                    level.id,
                                    event.target.value
                                  )
                                }
                                required
                              >
                                <option value="">
                                  Select {level.singular_label.toLowerCase()}
                                </option>
                                {options.map(option => (
                                  <option key={option.id} value={option.id}>
                                    {option.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                          {propertySelectionReady && (
                            <>
                              <div className="col-md-6">
                                <label
                                  className="form-label"
                                  htmlFor="property-search"
                                >
                                  Search property / unit
                                </label>
                                <input
                                  id="property-search"
                                  type="search"
                                  className="form-control"
                                  value={propertySearch}
                                  onChange={event =>
                                    setPropertySearch(event.target.value)
                                  }
                                  placeholder="Example: 101, A-34 or shop"
                                  autoComplete="off"
                                />
                                <small className="text-muted">
                                  {eligibleProperties.length} properties in this
                                  section
                                </small>
                              </div>
                              <div className="col-md-6">
                                <label
                                  className="form-label"
                                  htmlFor="propertyId"
                                >
                                  Property / unit
                                </label>
                                <select
                                  id="propertyId"
                                  className="form-select"
                                  name="propertyId"
                                  value={formData.propertyId}
                                  onChange={event =>
                                    selectProperty(event.target.value)
                                  }
                                  required
                                  disabled={propertySearchRequired}
                                >
                                  <option value="">
                                    {propertySearchRequired
                                      ? 'Type above to search'
                                      : 'Select property'}
                                  </option>
                                  {matchingProperties.map(property => (
                                    <option
                                      key={property.id}
                                      value={property.id}
                                    >
                                      {property.display_name ||
                                        property.property_number}
                                    </option>
                                  ))}
                                </select>
                                {!propertySearchRequired &&
                                  matchingProperties.length === 0 && (
                                    <small className="text-danger">
                                      No matching property found.
                                    </small>
                                  )}
                                {matchingProperties.length === 75 && (
                                  <small className="text-muted">
                                    Showing the first 75 matches. Refine your
                                    search if needed.
                                  </small>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </section>
                    )}
                    <div className="row">
                      {communityStructure.properties.length===0&&<div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="division" className="form-label">
                            Division
                          </label>
                          <select
                            className="form-select"
                            id="division"
                            name="division"
                            value={formData.division}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select Division</option>
                            <option value="A">A</option>
                            <option value="B">B</option>
                            <option value="C">C</option>
                            <option value="D">D</option>
                          </select>
                        </div>
                      </div>}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="ownershipType" className="form-label">
                            Ownership Type
                          </label>
                          <select
                            className="form-select"
                            id="ownershipType"
                            name="ownershipType"
                            value={formData.ownershipType}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select Ownership</option>
                            <option value="owner">Owner</option>
                            <option value="tenant">Tenant</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="alert alert-light border small">
                      <strong>How your information is used</strong>
                      <ul className="mb-0 mt-2">
                        <li>To create and administer your community account.</li>
                        <li>To provide resident, visitor, finance, booking and notification services enabled by your community.</li>
                        <li>To protect the platform and measure limited access analytics.</li>
                      </ul>
                    </div>
                    <div className="mb-3 form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="acceptedTerms"
                        name="acceptedTerms"
                        checked={formData.acceptedTerms}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="acceptedTerms">
                        I accept the{' '}
                        <NavLink to="/terms" target="_blank" rel="noopener noreferrer">
                          Terms of Service
                        </NavLink>{' '}
                        (version {legalVersions.terms.version}).
                      </label>
                    </div>
                    <div className="mb-3 form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="privacyConsentGiven"
                        name="privacyConsentGiven"
                        checked={formData.privacyConsentGiven}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="privacyConsentGiven">
                        I have read the{' '}
                        <NavLink to="/privacy" target="_blank" rel="noopener noreferrer">
                          Privacy Notice
                        </NavLink>{' '}
                        (version {legalVersions.privacy.version}) and consent to the described processing.
                      </label>
                    </div>
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-100 mb-3"
                      disabled={!formData.acceptedTerms || !formData.privacyConsentGiven}
                    >
                      Register
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="w-100"
                      onClick={() => setStep(2)}
                    >
                      Back to Community Selection
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
